// Helpers for preamble/notes content shared by the rich-text editor and the PDF renderers.
// The editor stores rich HTML; older saved values are plain text. These normalize both.

const HTML_TAG = /<\/?[a-z][\s\S]*>/i;
// a semicolon-terminated entity (&nbsp; &amp; &#160; …). The editor emits these; legacy
// plain text from a TextField holds literal characters (a bare "&" won't match).
const HTML_ENTITY = /&(?:#\d+|#x[0-9a-f]+|[a-z][a-z0-9]*);/i;

export function isHtml(value: string): boolean {
    return HTML_TAG.test(value) || HTML_ENTITY.test(value);
}

export function escapeHtml(value: string): string {
    return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}

// HTML used to seed the contentEditable editor. Legacy plain-text values become escaped text
// with their line breaks preserved as <br>.
export function preambleToEditorHtml(value: string): string {
    if(!value) return "";
    if(isHtml(value)) return value;
    return escapeHtml(value).replace(/\n/g, "<br>");
}

// tags that start a new line/paragraph in the notes. Everything else at the top level (text,
// b/i/u/em/strong/span/a…) is inline and stays on the current line.
const BLOCK_TAGS = new Set([
    "DIV", "P", "H1", "H2", "H3", "H4", "H5", "H6",
    "UL", "OL", "LI", "BLOCKQUOTE", "PRE", "TABLE", "HR", "SECTION", "ARTICLE",
]);

// Block-level HTML chunks for the PDF: one per line/paragraph, with inline formatting kept
// together on its line. The PDF paginator splits the array between pages but never within an
// item, so this lets long notes flow across pages without breaking mid-line.
export function preambleToBlocks(value: string): string[] {
    if(!value) return [];

    if(!isHtml(value)) {
        return value.split("\n").map(escapeHtml);
    }

    const doc = new DOMParser().parseFromString(value, "text/html");
    const blocks: string[] = [];
    let inline = "";

    const flushInline = () => {
        if(inline.trim()) blocks.push(inline);
        inline = "";
    };

    doc.body.childNodes.forEach(node => {
        if(node instanceof Element && BLOCK_TAGS.has(node.tagName)) {
            flushInline();
            blocks.push(node.outerHTML);
        } else if(node instanceof Element && node.tagName === "BR") {
            // a top-level <br> ends the current line.
            flushInline();
        } else if(node.nodeType === Node.TEXT_NODE) {
            inline += escapeHtml(node.textContent || "");
        } else if(node instanceof Element) {
            // inline element (b/i/u/em/strong/span/a…) — keep it on the current line.
            inline += node.outerHTML;
        }
    });
    flushInline();

    return blocks;
}

// whether the notes have any visible text (ignores empty tags like a stray <br>), for the
// "has preamble" tab indicator.
export function hasPreambleContent(value: string): boolean {
    if(!value) return false;
    if(!isHtml(value)) return value.trim() !== "";
    const doc = new DOMParser().parseFromString(value, "text/html");
    return (doc.body.textContent || "").trim() !== "";
}
